import React from "react";
import { motion } from "framer-motion";
import {
  ShieldCheck,
  LockKeyhole,
  UserRoundCheck,
  Eye,
  Database,
  Server,
  KeyRound,
  FileCheck2,
  Settings,
  AlertTriangle,
  ArrowRight,
  Check,
  HeartPulse,
  Mail,
} from "lucide-react";
import Navbar from "../../../DashBoardComponents/Navbar";

const SecurityPrivacy = () => {
  const securityLayers = [
    {
      icon: LockKeyhole,
      title: "Secure Access",
      description:
        "Access to accounts and healthcare information should be protected through appropriate authentication and access controls.",
    },
    {
      icon: Database,
      title: "Data Protection",
      description:
        "Information should be handled responsibly throughout its collection, storage, processing and lifecycle.",
    },
    {
      icon: Server,
      title: "Infrastructure",
      description:
        "Digital healthcare services depend on reliable infrastructure and security practices designed to reduce unnecessary risk.",
    },
    {
      icon: KeyRound,
      title: "Access Controls",
      description:
        "Appropriate permissions help ensure that information is available only to authorised users and systems.",
    },
  ];

  const privacyPrinciples = [
    {
      icon: UserRoundCheck,
      title: "Purpose & Transparency",
      description:
        "Users should have a clear understanding of how their information is used in connection with the relevant service.",
    },
    {
      icon: Eye,
      title: "Privacy Awareness",
      description:
        "Privacy considerations should be part of product design, development and operational processes.",
    },
    {
      icon: FileCheck2,
      title: "Responsible Handling",
      description:
        "Information should be handled according to applicable policies, requirements and legitimate purposes.",
    },
    {
      icon: ShieldCheck,
      title: "Continuous Protection",
      description:
        "Security and privacy practices should evolve as technology, threats and requirements change.",
    },
  ];

  const userControls = [
    "Review and manage relevant account information",
    "Use appropriate account security controls",
    "Understand applicable privacy practices",
    "Contact the appropriate support team with privacy questions",
    "Report potential security or privacy concerns",
    "Follow recommended security practices when using Doxzon services",
  ];

  const securityProcess = [
    {
      number: "01",
      title: "Identify",
      description:
        "Understand the information, systems and workflows that need protection.",
    },
    {
      number: "02",
      title: "Protect",
      description:
        "Apply appropriate security and privacy controls to reduce unnecessary risk.",
    },
    {
      number: "03",
      title: "Monitor",
      description:
        "Review systems and processes to identify potential issues and areas for improvement.",
    },
    {
      number: "04",
      title: "Respond",
      description:
        "Follow appropriate processes when security or privacy concerns are identified.",
    },
  ];

  return (
    <>
      <Navbar />

      <main className="bg-white text-slate-900 overflow-hidden">

        {/* =====================================================
            HERO
        ====================================================== */}
        <section className="relative overflow-hidden bg-[#f4fbfc]">

          <div className="absolute -top-40 -right-40 w-[650px] h-[650px] rounded-full bg-teal-200/25 blur-3xl" />

          <div className="absolute -bottom-40 -left-40 w-[550px] h-[550px] rounded-full bg-cyan-200/25 blur-3xl" />

          <div
            className="absolute inset-0 opacity-[0.025]"
            style={{
              backgroundImage:
                "linear-gradient(#0f172a 1px, transparent 1px), linear-gradient(90deg, #0f172a 1px, transparent 1px)",
              backgroundSize: "48px 48px",
            }}
          />

          <div className="relative max-w-7xl mx-auto px-6 pt-20 pb-24 lg:pt-28 lg:pb-32 grid lg:grid-cols-2 gap-16 items-center">

            {/* HERO CONTENT */}
            <motion.div
              initial={{ opacity: 0, x: -35 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
            >

              <div className="inline-flex items-center gap-2 rounded-full bg-white border border-teal-100 px-4 py-2 shadow-sm">

                <span className="w-2 h-2 rounded-full bg-teal-500" />

                <span className="text-[11px] font-bold uppercase tracking-[0.2em] text-teal-700">
                  Security & Privacy
                </span>

              </div>

              <h1 className="font-sora text-5xl md:text-6xl lg:text-[62px] font-medium tracking-[-0.055em] leading-[1.03] mt-7">

                Your information
                <span className="block text-teal-500 mt-2">
                  deserves protection.
                </span>

              </h1>

              <p className="text-slate-600 text-lg md:text-xl leading-8 mt-7 max-w-xl">
                Security and privacy are important parts of creating
                trustworthy digital healthcare experiences. Doxzon aims to
                approach both with care throughout its products and
                operations.
              </p>

              <div className="flex flex-wrap gap-3 mt-9">

                <button className="inline-flex items-center gap-2 rounded-xl bg-slate-950 text-white px-6 h-12 text-sm font-semibold hover:bg-slate-800 transition">

                  Our Security Approach

                  <ArrowRight size={16} />

                </button>

                <button className="inline-flex items-center gap-2 rounded-xl bg-white border border-slate-200 text-slate-800 px-6 h-12 text-sm font-semibold hover:border-teal-200 transition">

                  Privacy Policy

                </button>

              </div>

            </motion.div>

            {/* HERO VISUAL */}
            <motion.div
              initial={{ opacity: 0, x: 35, scale: 0.96 }}
              animate={{ opacity: 1, x: 0, scale: 1 }}
              transition={{ duration: 0.9 }}
              className="relative"
            >

              <div className="absolute -inset-8 rounded-[45px] bg-teal-200/30 blur-3xl" />

              <div className="relative rounded-[32px] bg-white border border-slate-100 shadow-[0_30px_80px_rgba(15,23,42,0.14)] p-7">

                <div className="flex items-center justify-between">

                  <div>

                    <p className="text-[10px] uppercase tracking-[0.2em] text-slate-400">
                      Doxzon Trust Framework
                    </p>

                    <h3 className="font-sora text-xl font-semibold mt-1">
                      Protected Healthcare
                    </h3>

                  </div>

                  <div className="w-12 h-12 rounded-2xl bg-teal-50 grid place-items-center">

                    <ShieldCheck
                      size={24}
                      className="text-teal-600"
                    />

                  </div>

                </div>

                {/* SECURITY VISUAL */}
                <div className="relative flex justify-center my-10">

                  <div className="absolute w-56 h-56 rounded-full border border-teal-100" />

                  <div className="absolute w-40 h-40 rounded-full border border-teal-100" />

                  <div className="relative z-10 w-24 h-24 rounded-[28px] bg-slate-950 grid place-items-center shadow-xl">

                    <LockKeyhole
                      size={38}
                      className="text-teal-400"
                    />

                  </div>

                </div>

                <div className="grid grid-cols-2 gap-3">

                  {[
                    [Database, "Data Protection"],
                    [UserRoundCheck, "Secure Access"],
                    [Server, "Infrastructure"],
                    [Eye, "Privacy"],
                  ].map(([Icon, title]) => (

                    <div
                      key={title}
                      className="rounded-2xl bg-slate-50 border border-slate-100 p-4"
                    >

                      <div className="w-9 h-9 rounded-xl bg-white grid place-items-center">

                        <Icon
                          size={17}
                          className="text-teal-600"
                        />

                      </div>

                      <p className="text-xs font-semibold mt-3">
                        {title}
                      </p>

                    </div>

                  ))}

                </div>

              </div>

            </motion.div>

          </div>

        </section>

        {/* =====================================================
            SECURITY
        ====================================================== */}
        <section className="py-24 md:py-28">

          <div className="max-w-7xl mx-auto px-6">

            <div className="max-w-2xl">

              <span className="text-[11px] font-bold uppercase tracking-[0.22em] text-teal-600">
                Security
              </span>

              <h2 className="font-sora text-4xl md:text-5xl font-medium mt-4">

                Security at every
                <span className="block text-teal-500">
                  layer.
                </span>

              </h2>

              <p className="text-slate-600 leading-7 mt-5">
                Healthcare technology requires thoughtful protection across
                accounts, data, infrastructure and access.
              </p>

            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-5 mt-12">

              {securityLayers.map((item, index) => {

                const Icon = item.icon;

                return (
                  <motion.div
                    key={item.title}
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: index * 0.07 }}
                    whileHover={{ y: -6 }}
                    className="rounded-3xl border border-slate-100 bg-slate-50/70 p-7 hover:bg-white hover:shadow-xl hover:border-teal-100 transition"
                  >

                    <div className="w-12 h-12 rounded-2xl bg-teal-50 grid place-items-center">

                      <Icon
                        size={22}
                        className="text-teal-600"
                      />

                    </div>

                    <h3 className="font-sora text-lg font-semibold mt-6">
                      {item.title}
                    </h3>

                    <p className="text-slate-500 text-sm leading-7 mt-3">
                      {item.description}
                    </p>

                  </motion.div>
                );

              })}

            </div>

          </div>

        </section>

        {/* =====================================================
            PRIVACY
        ====================================================== */}
        <section className="py-24 md:py-28 bg-[#f6fbfc]">

          <div className="max-w-7xl mx-auto px-6">

            <div className="text-center max-w-2xl mx-auto">

              <span className="text-[11px] font-bold uppercase tracking-[0.22em] text-teal-600">
                Privacy
              </span>

              <h2 className="font-sora text-4xl md:text-5xl font-medium mt-4">

                Privacy should be
                <span className="text-teal-500">
                  {" "}understood.
                </span>

              </h2>

              <p className="text-slate-600 leading-7 mt-5">
                Clear and responsible privacy practices help build trust
                between people and the technology they use.
              </p>

            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-5 mt-12">

              {privacyPrinciples.map((item, index) => {

                const Icon = item.icon;

                return (
                  <motion.div
                    key={item.title}
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: index * 0.07 }}
                    whileHover={{ y: -6 }}
                    className="rounded-3xl bg-white border border-slate-100 p-7 hover:shadow-xl hover:border-teal-100 transition"
                  >

                    <div className="w-12 h-12 rounded-2xl bg-teal-50 grid place-items-center">

                      <Icon
                        size={22}
                        className="text-teal-600"
                      />

                    </div>

                    <h3 className="font-sora text-lg font-semibold mt-6">
                      {item.title}
                    </h3>

                    <p className="text-slate-500 text-sm leading-7 mt-3">
                      {item.description}
                    </p>

                  </motion.div>

                );

              })}

            </div>

          </div>

        </section>

        {/* =====================================================
            HOW WE PROTECT
        ====================================================== */}
        <section className="py-24 md:py-28">

          <div className="max-w-6xl mx-auto px-6 grid lg:grid-cols-2 gap-14 items-center">

            <div>

              <span className="text-[11px] font-bold uppercase tracking-[0.22em] text-teal-600">
                Our Approach
              </span>

              <h2 className="font-sora text-4xl md:text-5xl font-medium mt-4">

                Protection is a
                <span className="block text-teal-500">
                  continuous process.
                </span>

              </h2>

              <p className="text-slate-600 leading-8 mt-6">
                Security isn't something that happens once and then gets
                forgotten. Systems, processes and practices need to be
                reviewed as technology and risks evolve.
              </p>

              <div className="mt-8 rounded-2xl bg-teal-50 border border-teal-100 p-5">

                <div className="flex gap-4">

                  <div className="w-10 h-10 rounded-xl bg-white grid place-items-center shrink-0">

                    <ShieldCheck
                      size={19}
                      className="text-teal-600"
                    />

                  </div>

                  <div>

                    <h3 className="font-semibold">
                      Security-minded development
                    </h3>

                    <p className="text-teal-800/70 text-sm leading-6 mt-1">
                      Security and privacy considerations should be included
                      throughout the product and operational lifecycle.
                    </p>

                  </div>

                </div>

              </div>

            </div>

            {/* PROCESS */}
            <div className="space-y-4">

              {securityProcess.map((step, index) => (

                <motion.div
                  key={step.number}
                  initial={{ opacity: 0, x: 20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.08 }}
                  className="flex gap-5 rounded-2xl border border-slate-100 bg-slate-50 p-5 hover:bg-white hover:shadow-lg hover:border-teal-100 transition"
                >

                  <div className="w-12 h-12 rounded-xl bg-slate-950 text-teal-400 grid place-items-center shrink-0">

                    <span className="text-xs font-bold">
                      {step.number}
                    </span>

                  </div>

                  <div>

                    <h3 className="font-sora font-semibold">
                      {step.title}
                    </h3>

                    <p className="text-slate-500 text-sm leading-6 mt-1">
                      {step.description}
                    </p>

                  </div>

                </motion.div>

              ))}

            </div>

          </div>

        </section>

        {/* =====================================================
            USER SECURITY
        ====================================================== */}
        <section className="py-24 md:py-28 bg-slate-950 text-white">

          <div className="max-w-6xl mx-auto px-6 grid lg:grid-cols-2 gap-14 items-center">

            <div>

              <span className="text-[11px] font-bold uppercase tracking-[0.22em] text-teal-400">
                Your Role
              </span>

              <h2 className="font-sora text-4xl md:text-5xl font-medium mt-4">

                Security is
                <span className="block text-teal-400">
                  everyone's responsibility.
                </span>

              </h2>

              <p className="text-slate-400 leading-8 mt-6">
                Good security also depends on the choices users make when
                creating and managing their accounts.
              </p>

            </div>

            <div className="space-y-3">

              {userControls.map((item) => (

                <motion.div
                  key={item}
                  whileHover={{ x: 5 }}
                  className="flex items-center gap-4 rounded-2xl bg-white/[0.05] border border-white/10 p-4"
                >

                  <span className="w-8 h-8 rounded-full bg-teal-500/10 text-teal-400 grid place-items-center shrink-0">

                    <Check size={15} />

                  </span>

                  <span className="text-sm text-slate-300">
                    {item}
                  </span>

                </motion.div>

              ))}

            </div>

          </div>

        </section>

        {/* =====================================================
            SECURITY NOTICE
        ====================================================== */}
        <section className="py-20 bg-[#f6fbfc]">

          <div className="max-w-5xl mx-auto px-6">

            <div className="rounded-3xl bg-white border border-slate-100 p-7 md:p-9">

              <div className="flex gap-5">

                <div className="w-12 h-12 rounded-2xl bg-amber-50 grid place-items-center shrink-0">

                  <AlertTriangle
                    size={22}
                    className="text-amber-600"
                  />

                </div>

                <div>

                  <h2 className="font-sora text-xl md:text-2xl font-semibold">
                    Never share sensitive information through an insecure
                    channel.
                  </h2>

                  <p className="text-slate-500 text-sm leading-7 mt-3">
                    If you believe you have discovered a security or privacy
                    issue, use the appropriate official Doxzon support or
                    security contact rather than sharing sensitive information
                    publicly.
                  </p>

                  <button className="inline-flex items-center gap-2 text-teal-600 text-sm font-semibold mt-5">

                    Report a concern

                    <ArrowRight size={15} />

                  </button>

                </div>

              </div>

            </div>

          </div>

        </section>

        {/* =====================================================
            LEGAL / PRIVACY DOCUMENTS
        ====================================================== */}
        <section className="py-24">

          <div className="max-w-6xl mx-auto px-6">

            <div className="text-center max-w-2xl mx-auto">

              <span className="text-[11px] font-bold uppercase tracking-[0.22em] text-teal-600">
                Important Information
              </span>

              <h2 className="font-sora text-4xl md:text-5xl font-medium mt-4">

                Privacy & security
                <span className="text-teal-500">
                  {" "}resources.
                </span>

              </h2>

            </div>

            <div className="grid md:grid-cols-3 gap-5 mt-12">

              {[
                {
                  icon: FileCheck2,
                  title: "Privacy Policy",
                  description:
                    "Learn about the applicable privacy practices and information handling principles.",
                },
                {
                  icon: ShieldCheck,
                  title: "Security Information",
                  description:
                    "Learn more about Doxzon's approach to security and responsible technology.",
                },
                {
                  icon: Settings,
                  title: "Account Settings",
                  description:
                    "Review available account and privacy-related controls.",
                },
              ].map((item, index) => {

                const Icon = item.icon;

                return (
                  <motion.div
                    key={item.title}
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: index * 0.07 }}
                    whileHover={{ y: -5 }}
                    className="rounded-3xl border border-slate-100 bg-white p-7 shadow-sm hover:shadow-xl hover:border-teal-100 transition"
                  >

                    <div className="w-12 h-12 rounded-2xl bg-teal-50 grid place-items-center">

                      <Icon
                        size={22}
                        className="text-teal-600"
                      />

                    </div>

                    <h3 className="font-sora text-xl font-semibold mt-6">
                      {item.title}
                    </h3>

                    <p className="text-slate-500 text-sm leading-7 mt-3">
                      {item.description}
                    </p>

                    <button className="inline-flex items-center gap-2 text-teal-600 text-sm font-semibold mt-6">

                      Learn more

                      <ArrowRight size={15} />

                    </button>

                  </motion.div>

                );

              })}

            </div>

          </div>

        </section>

        {/* =====================================================
            CONTACT
        ====================================================== */}
        <section className="px-6 pb-24">

          <div className="relative overflow-hidden max-w-7xl mx-auto rounded-[32px] bg-[#f4fbfc] border border-teal-50 px-8 py-16 md:px-16">

            <div className="absolute -top-24 -right-24 w-64 h-64 rounded-full bg-teal-200/30 blur-3xl" />

            <div className="absolute -bottom-28 -left-20 w-72 h-72 rounded-full bg-cyan-200/30 blur-3xl" />

            <div className="relative grid lg:grid-cols-2 gap-10 items-center">

              <div>

                <span className="text-[11px] font-bold uppercase tracking-[0.22em] text-teal-600">
                  Privacy & Security Questions
                </span>

                <h2 className="font-sora text-4xl md:text-5xl font-medium mt-4">

                  Need more
                  <span className="block text-teal-500">
                    information?
                  </span>

                </h2>

                <p className="text-slate-600 leading-7 mt-5 max-w-xl">
                  If you have a question about privacy, security or how your
                  information is handled, contact the appropriate Doxzon team.
                </p>

              </div>

              <div className="flex lg:justify-end">

                <button className="inline-flex items-center gap-2 rounded-xl bg-slate-950 text-white px-7 h-14 font-semibold hover:bg-teal-500 transition">

                  <Mail size={18} />

                  Contact Doxzon

                  <ArrowRight size={17} />

                </button>

              </div>

            </div>

          </div>

        </section>

      </main>
    </>
  );
};

export default SecurityPrivacy;